/**
 * Чотири скрині, які стоять біля гравця в hardcore.
 *
 * Kept here rather than beside the code that puts them down so it can be
 * read back and checked: what matters about this kit is not that four
 * chests appear, it is that a player who opens them can *hold the island* -
 * two suits of armour, something to fight with at both ranges, and enough
 * food and batteries to still be flying in an hour. That is a claim about
 * the contents, and a claim about contents belongs somewhere a check can
 * reach.
 */
import { PlainItemId } from "../block/items.js";
import { TREBUCHET_BASE, BlockId } from "../block/registry.js";
import { CHEST_SLOTS } from "./containers.js";

export interface KitStack { readonly id: number; readonly count: number; }

/** One suit repeated until the скриня is full: helm, mail, bracers, boots. */
const suits = (pieces: readonly number[]): KitStack[] =>
  Array.from({ length: CHEST_SLOTS }, (_, i) => ({
    id: pieces[i % pieces.length]!,
    count: 1,
  }));

export const CAMP_KIT: readonly (readonly KitStack[])[] = [
  // Iron, as many suits as a скриня holds.
  suits([
    PlainItemId.Helm, PlainItemId.Mail,
    PlainItemId.Bracers, PlainItemId.Boots,
  ]),
  // And луска, which is the better suit and the one nobody has yet.
  suits([
    PlainItemId.ScaleHelm, PlainItemId.ScaleMail,
    PlainItemId.ScaleBracers, PlainItemId.ScaleBoots,
  ]),
  // What to fight with, and what to fight from behind.
  [
    { id: PlainItemId.IronSabre, count: 1 },
    { id: PlainItemId.IronSabre, count: 1 },
    { id: PlainItemId.WoodenShield, count: 1 },
    { id: PlainItemId.Bow, count: 1 },
    { id: PlainItemId.Bow, count: 1 },
    { id: PlainItemId.Arrow, count: 64 },
    { id: PlainItemId.Arrow, count: 64 },
    { id: PlainItemId.Arrow, count: 64 },
    { id: TREBUCHET_BASE, count: 8 },
    { id: PlainItemId.Firebolt, count: 32 },
    { id: PlainItemId.Firebolt, count: 32 },
    { id: PlainItemId.Kresalo, count: 1 },
  ],
  // Food, and the drone with everything it eats.
  [
    { id: PlainItemId.Pork, count: 32 },
    { id: PlainItemId.Beef, count: 32 },
    { id: PlainItemId.Chicken, count: 32 },
    { id: PlainItemId.Drone, count: 4 },
    { id: PlainItemId.Remote, count: 2 },
    { id: PlainItemId.Battery, count: 16 },
    { id: PlainItemId.Firebolt, count: 32 },
  ],
];

/** A скриня of one thing, as full as it goes. */
const full = (id: number, count: number): KitStack[] =>
  Array.from({ length: CHEST_SLOTS }, () => ({ id, count }));

/**
 * Десять скринь біля прапора, у «Сучасній війні» з хардкором.
 *
 * "Видаєм гравцям скрині з ресурсами ... це має допомогти швидко
 * підготуватись до бою." Ten minutes of preparing is not long enough to
 * cut a forest, dig a quarry and forge a suit, so the ten hold what the
 * preparing is spent on: oil and rubber for the machines, tools to build
 * faster, a suit and weapons each, food, light, and the siege kit. Not
 * адамантій: the one tool that takes a прапор down is still earned.
 */
export const WAR_KIT: readonly (readonly KitStack[])[] = [
  // Нафта and гума, for the machines: "заміни камінь та дерево у скринях
  // на нафту та гуму". Wood and stone are what the map has plenty of.
  full(PlainItemId.CrudeOil, 64),
  full(PlainItemId.CrudeOil, 64),
  full(PlainItemId.Rubber, 64),
  full(PlainItemId.Rubber, 64),
  // Tools to build it faster, and light to build by.
  [
    ...Array.from({ length: 6 }, () => ({ id: PlainItemId.IronPickaxe, count: 1 })),
    ...Array.from({ length: 6 }, () => ({ id: PlainItemId.IronAxe, count: 1 })),
    ...Array.from({ length: 8 }, () => ({ id: BlockId.Torch, count: 64 })),
    { id: BlockId.Glass, count: 64 },
    { id: BlockId.Glass, count: 64 },
  ],
  // A suit each.
  suits([
    PlainItemId.Helm, PlainItemId.Mail,
    PlainItemId.Bracers, PlainItemId.Boots,
  ]),
  // Something to fight with, at both ranges.
  [
    ...Array.from({ length: 6 }, () => ({ id: PlainItemId.IronSabre, count: 1 })),
    ...Array.from({ length: 4 }, () => ({ id: PlainItemId.WoodenShield, count: 1 })),
    ...Array.from({ length: 4 }, () => ({ id: PlainItemId.Bow, count: 1 })),
    ...Array.from({ length: 8 }, () => ({ id: PlainItemId.Arrow, count: 64 })),
  ],
  // Food for an evening.
  [
    ...Array.from({ length: 9 }, () => ({ id: PlainItemId.Pork, count: 32 })),
    ...Array.from({ length: 9 }, () => ({ id: PlainItemId.Beef, count: 32 })),
    ...Array.from({ length: 9 }, () => ({ id: PlainItemId.Chicken, count: 32 })),
  ],
  // The siege: a требушет and what it throws.
  [
    { id: TREBUCHET_BASE, count: 8 },
    ...Array.from({ length: 6 }, () => ({ id: PlainItemId.Firebolt, count: 32 })),
    { id: PlainItemId.Kresalo, count: 1 },
  ],
  // And iron to forge more of whatever ran out first.
  full(PlainItemId.IronIngot, 64),
];
